function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', {bubbles: true});
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {'Content-Type': 'application/json', Accept: `application/${type}`},
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      this.preserveSummaryViewportPosition(summaryElement, () => {
        if (!isOpen) this.closeSiblingSubmenus(detailsElement);
      });
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  getScrollContainer(element) {
    let node = element?.parentElement;

    while (node && node !== document.body) {
      const styles = window.getComputedStyle(node);
      const overflowY = styles.overflowY;
      const canScroll =
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
        && node.scrollHeight > node.clientHeight;
      if (canScroll) return node;
      node = node.parentElement;
    }

    return window;
  }

  getTopInContainer(element, scrollContainer) {
    const rect = element.getBoundingClientRect();
    if (scrollContainer === window) return rect.top;
    return rect.top - scrollContainer.getBoundingClientRect().top;
  }

  preserveSummaryViewportPosition(summaryElement, mutate) {
    if (!(summaryElement instanceof HTMLElement)) {
      mutate();
      return;
    }

    const scrollContainer = this.getScrollContainer(summaryElement);
    const beforeTop = this.getTopInContainer(summaryElement, scrollContainer);

    mutate();

    window.requestAnimationFrame(() => {
      const afterTop = this.getTopInContainer(summaryElement, scrollContainer);
      const delta = afterTop - beforeTop;
      if (Math.abs(delta) < 1) return;

      if (scrollContainer === window) {
        window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
      } else {
        scrollContainer.scrollTop += delta;
      }
    });
  }

  closeSiblingSubmenus(detailsElement) {
    const menuItem = detailsElement.parentElement;
    const menuList = menuItem?.parentElement;
    if (!menuList) return;

    Array.from(menuList.children).forEach((siblingItem) => {
      if (siblingItem === menuItem) return;

      const siblingDetails =
        siblingItem.tagName === 'DETAILS'
          ? siblingItem
          : Array.from(siblingItem.children).find((child) => child.tagName === 'DETAILS');
      if (!siblingDetails || !siblingDetails.hasAttribute('open')) return;

      this.closeSubmenu(siblingDetails, { animate: false });
    });
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement, { animate = true } = {}) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));

    detailsElement.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    detailsElement.querySelectorAll('details').forEach((submenuDetails) => {
      submenuDetails.classList.remove('menu-opening');
      submenuDetails.querySelector('summary')?.setAttribute('aria-expanded', false);
      if (!animate) submenuDetails.removeAttribute('open');
    });

    if (animate) {
      this.closeAnimation(detailsElement);
    } else {
      detailsElement.removeAttribute('open');
    }
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.syncLifestyleImagesToSlider(this.slider);
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  syncLifestyleImagesToSlider(sliderElement, lifestyleUlId = 'SliderLifestyleImages') {
    const lifestyleUl = document.getElementById(lifestyleUlId);
    if (lifestyleUl && sliderElement) {
      // 1. 先移除舊的 lifestyle li
      sliderElement.querySelectorAll('[data-from-lifestyle="true"]').forEach(li => li.remove());
      // 2. 加入最新一批
      lifestyleUl.querySelectorAll('li').forEach(li => {
        const clone = li.cloneNode(true);
        clone.setAttribute('data-from-lifestyle', 'true');
        sliderElement.appendChild(clone);
      });
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          {once: true},
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

/**
 * VariantSelects
 * --------------------------------------------------
 * 掌管產品變體（特別是顏色 swatch）在卡片／商品頁上的初始化、同步與圖片切換。
 * 核心能力：
 * 1) 初始化：從 URL ?variant=、上次選擇、或預設選項推導初始狀態
 * 2) 同步：radio/selector 屬性與 UI 樣式、[checked] attribute、ATC 表單的 variant id、URL 參數
 * 3) 媒體：切換主圖（優先 media-gallery；退回縮圖點擊；最後直接換 <img>）
 * 4) Swatch/Tag 捲動區域的寬度、箭頭顯示與滑動控制
 * 5) 互斥機制：避免初始化／晚到請求覆蓋使用者剛剛的手動選擇
 */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * 元件掛載時：綁定各類事件、初始化變體與圖片、監聽 swatch 改變。
   */
  connectedCallback() {
    this.bindColorScrollEvents();
    this.bindTagScrollEvents();
    this.bindSwatchNameToggle();
    this.bindSwatchOuterClickArea();
    this.bindSwatchPreviewEvents();
    this.initializeVariantFromUrlOrDefault();
    this.switchProductVariant();
    this.watchColorSwatchAndUpdateSceneImage();

    // 統一監聽 change：同步屬性、紀錄手動操作時間、發佈事件（除非被抑制）
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      // 若是 radio：同時同步 property 與 [checked] attribute，並清掉同組其他顆
      if (target && target.tagName === 'INPUT' && target.type === 'radio') {
        this.setRadioChecked(target);
        this.updateVariantId(target)
      }

      // 紀錄最近一次「手動」選擇時間（供初始化判斷是否跳過覆寫）
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      this._lastUserChangeAt = now;
      window.__manualVariantPickTs = now; // 全域一份，避免 section 重新掛載遺失

      // 保存最近的選中的 option id（陣列）
      window.__lastVariantOptions = this.selectedOptionValues;

      // 可由程式發出的 CustomEvent 指定 { detail: { suppressPublish: true } } 以抑制 publish
      const shouldSuppress = event instanceof CustomEvent && event.detail?.suppressPublish;
      if (shouldSuppress) return;

      // 標記：已經執行過一次使用者驅動的互動（hydrated）
      window.__variantSelectsHydrated = true;

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  /**
   * 讓 swatch-outer2 整塊都可點擊：點外圍空白也會選中對應 radio。
   */
  bindSwatchOuterClickArea() {
    const wrappers = this.querySelectorAll('.swatch-outer2');
    wrappers.forEach((wrapper) => {
      if (wrapper.dataset.clickBound === 'true') return;
      wrapper.dataset.clickBound = 'true';

      wrapper.addEventListener('click', (event) => {
        if (event.target.closest('label, input, button, a, select, textarea')) {
          return;
        }

        const radio = wrapper.querySelector('.swatch-input__input[type="radio"][data-option-value-id]');
        if (!radio || radio.disabled || radio.checked) return;

        radio.click();
      });
    });
  }

  /**
   * 初始化當前顏色／變體來源優先序：
   * 1) 若已 hydrated、且目前已有選中或在鎖定時間內，不覆蓋
   * 2) URL ?variant= 指定
   * 3) 上一次全域紀錄的 option（window.__lastVariantOptions）
   * 4) 標記為 checked 的 radio
   * 5) fallback：第一顆顏色＋嘗試更新主圖
   */
  initializeVariantFromUrlOrDefault() {
    // 若已初始化過、或剛有人手動改過，就不要再動
    const anyChecked = this.querySelector('input[type="radio"][data-option-value-id]:checked');
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const LOCK_MS = 1500;
    const manualTs = window.__manualVariantPickTs || this._lastUserChangeAt || 0;
    const lockActive = manualTs && (now - manualTs) < LOCK_MS;

    if ((window.__variantSelectsHydrated === true) && (anyChecked || lockActive)) {
      return;
    }

    const variants = this.getVariantData();
    if (!variants?.length) {
      this.initializeDefaultColorAndImage();
      return;
    }

    // 1) URL ?variant=
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (variantId) {
      const matched = variants.find((variant) => variant.id.toString() === variantId);
      if (matched) {
        const colorValue = matched.options?.[0];
        if (colorValue) {
          const targetRadio = this.querySelector(
            `input[type="radio"][data-option-value-id][value="${CSS.escape(colorValue)}"]`,
          );
          if (targetRadio) {
            this.setRadioChecked(targetRadio);
            this.dispatchChange(targetRadio, {suppressPublish: this.shouldSuppressAutoChange()});
            return;
          }
        }
      }
    }

    // 2) 上次的 option（只取第一階層：顏色）
    const lastSelectedOptionId = Array.isArray(window.__lastVariantOptions)
      ? window.__lastVariantOptions[0]
      : null;

    if (lastSelectedOptionId) {
      const fallbackRadio = this.querySelector(`input[data-option-value-id="${CSS.escape(lastSelectedOptionId)}"]`);
      if (fallbackRadio) {
        this.setRadioChecked(fallbackRadio);
        this.dispatchChange(fallbackRadio, {suppressPublish: this.shouldSuppressAutoChange()});
        return;
      }
    }

    // 3) 有預設 checked 的 radio
    // const precheckedRadio = this.querySelector('input[type="radio"][data-option-value-id]:checked');
    // if (precheckedRadio) {
    //   this.setRadioChecked(precheckedRadio);
    //   this.dispatchChange(precheckedRadio, {suppressPublish: this.shouldSuppressAutoChange()});
    //   return;
    // }

    // 4) fallback：選第 1 顆顏色、嘗試更新主圖並觸發 change
    this.initializeDefaultColorAndImage();
  }

  /**
   * 設定指定 radio 為選中，並清除同組其他 radio 的 checked 與 [checked]。
   * @param {HTMLInputElement} radio
   */
  setRadioChecked(radio) {
    if (!radio) return;

    const group =
      radio.closest('.product-form__input')?.querySelectorAll('input[type="radio"][data-option-value-id]') ||
      this.querySelectorAll('input[type="radio"][data-option-value-id]');

    group.forEach((input) => {
      if (input === radio) {
        input.checked = true;
        input.setAttribute('checked', '');
      } else {
        input.checked = false;
        input.removeAttribute('checked');
      }
    });
  }

  /**
   * 取得變體資料，讀取順序：
   * 1) 元件內 <script data-variants>
   * 2) 元件 dataset.variants
   * 3) 全域 product-info 或文件內第一個 <script data-variants>
   * @returns {Array|null}
   */
  getVariantData() {
    if (this._variantData) return this._variantData;

    const parseJson = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.error('Failed to parse variant data', error);
        return null;
      }
    };

    const script = this.querySelector('script[data-variants]');
    const fromScript = parseJson(script?.textContent?.trim());
    if (fromScript?.length) {
      this._variantData = fromScript;
      return this._variantData;
    }

    const fromDataset = parseJson(this.dataset?.variants);
    if (fromDataset?.length) {
      this._variantData = fromDataset;
      return this._variantData;
    }

    const globalScript = document.querySelector('product-info script[data-variants]')
      || document.querySelector('script[data-variants]');
    const fromGlobal = parseJson(globalScript?.textContent?.trim());
    if (fromGlobal?.length) {
      this._variantData = fromGlobal;
      return this._variantData;
    }

    return null;
  }

  /**
   * 以原生/自訂事件觸發 change；可指定 suppressPublish 以避免對外 publish。
   * @param {Element} target
   * @param {{suppressPublish?: boolean}} opts
   */
  dispatchChange(target, {suppressPublish = false} = {}) {
    if (!target) return;

    const event = suppressPublish
      ? new CustomEvent('change', {bubbles: true, detail: {suppressPublish: true}})
      : new Event('change', {bubbles: true});

    target.dispatchEvent(event);
  }

  /**
   * 是否需要抑制初始化造成的 publish。
   * @returns {boolean}
   */
  shouldSuppressAutoChange() {
    return window.__variantSelectsHydrated === true;
  }

  /**
   * 無 URL/上次紀錄/預設時：選取第一顆顏色、嘗試找對應變體圖片更新主圖、並觸發一次 change。
   */
  initializeDefaultColorAndImage() {
    const firstBlock = this.closest('.card-block-first');
    if (!firstBlock) {
      return;
    }

    // 1) 找到顏色 swatch 群組（用 data-option-value-id 的 radio）
    const colorRadios = Array.from(this.querySelectorAll('input[type="radio"][data-option-value-id]'));
    if (!colorRadios.length) return;

    // 2) 獲取產品變體資料
    const variants = this.getVariantData();

    // 3) 根據 collection metafields 獲取預設顏色設定
    const collectionDefaultColors = this.getCollectionDefaultColors();

    // 4) 篩選出產品實際擁有的顏色變體
    const availableColors = this.getAvailableColors(variants, collectionDefaultColors);

    // 5) 隨機選擇一個顏色
    let selectedColor = null;
    if(collectionDefaultColors.length > 0) {
      selectedColor = this.selectRandomColor(availableColors);
    }

    // 6) 找到對應的 radio button 並選取
    const targetRadio = colorRadios.find(radio => radio.value === selectedColor) || colorRadios[0];
    this.setRadioChecked(targetRadio);

    const colorValue = targetRadio.value;

    // 找到該顏色下「可售」優先的變體（找不到就退回同色第一個）
    const matched =
      variants?.find((variant) => variant.options?.[0] === colorValue && variant.available)
      || variants?.find((variant) => variant.options?.[0] === colorValue);

    // 有對應圖片：更新卡片主圖（避免重複請求以 dataset.currentSrc 判斷）
    if (matched?.featured_media?.preview_image) {
      const img = this.closest('.card--media')?.querySelector('.card__media img');
      if (img) {
        const nextSrc = matched.featured_media.preview_image.src;
        if (img.dataset.currentSrc !== nextSrc) {
          img.src = `${nextSrc}?width=1946`;
          img.srcset = [
            246, 360, 533, 720, 940, 1066, 1206, 1346, 1646, 1946,
          ].map((width) => `${nextSrc}?width=${width} ${width}w`).join(', ');
          img.sizes = img.sizes || '(min-width: 750px) 50vw, 100vw';
          img.decoding = 'async';
          img.dataset.currentSrc = nextSrc;
        }
      }
    }

    // 觸發一次 change 讓其他監聽（價格/庫存/變體 id）跟著更新
    this.dispatchChange(targetRadio, {suppressPublish: this.shouldSuppressAutoChange()});
  }

  // 新增的方法 - 請添加到 VariantSelects 類中
  getCollectionDefaultColors() {
    // 優先從 metafields 獲取 collection 顏色設定
    let collectionColors = this.dataset.collectionColors;
    if (collectionColors != 'null' && collectionColors) {
      collectionColors = JSON.parse(collectionColors);
      return collectionColors.map(color => {
        // 如果顏色是 hex 值，可能需要轉換為顏色名稱
        // 這裡假設 metafields 存儲的是顏色名稱
        return typeof color === 'string' ? color : color.label;
      });
    }

    return [];
  }

  getAvailableColors(variants, defaultColors) {
    // 獲取產品實際擁有的所有顏色
    const availableColors = new Set();
    variants.forEach(variant => {
      if (variant.available && variant.options[0]) {
        availableColors.add(variant.options[0]);
      }
    });

    // 篩選出在預設顏色中且產品實際擁有的顏色
    const filteredColors = defaultColors.filter(color => availableColors.has(color));

    // 如果沒有匹配的顏色，返回所有可用的顏色
    return filteredColors.length > 0 ? filteredColors : Array.from(availableColors);
  }

  selectRandomColor(availableColors) {
    if (availableColors.length === 0) return null;

    // 簡單的偽隨機數生成器
    const randomIndex = Math.floor(Math.random() * availableColors.length);

    return availableColors[randomIndex];
  }

  /**
   * 同步下拉／radio 的選中狀態到 DOM 屬性與 UI 文案（例如選中顏色名稱）。
   * @param {{target: Element}} param0
   */
  updateSelectionMetadata({target}) {
    const {value, tagName} = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      // 清掉舊的 selected attribute，設定目前選中的 option
      Array.from(target.options)
      .find((option) => option.getAttribute('selected'))
      ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      // 同步 dropdown 的 swatch 預覽背景與 focal point
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
      .closest('.product-form__input')
      .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;

      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      // 同步目前被選的顏色名稱到 UI
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  /**
   * 綁定「顏色 swatch 水平捲動列」：寬度計算與左右箭頭顯示/滑動。
   * 每 200ms 檢查尺寸變化；1s 後綁定箭頭 click。
   */
  bindColorScrollEvents() {
    this.querySelectorAll('.swatch-scroll-container').forEach(container => {
      const refresh = () => {
        this.clampSwatchWidth(container);
        this.updateArrows(container);
      };
      const timer = setInterval(refresh, 200);
      refresh();

      const bindArrows = () => {
        const swatchInner = container.closest('.swatch-inner');
        if (!swatchInner) {
          return;
        }
        const leftArrow = swatchInner.querySelector('.swatch-arrow.left');
        const rightArrow = swatchInner.querySelector('.swatch-arrow.right');
        if (leftArrow) {
          leftArrow.onclick = () => container.scrollBy({left: -100, behavior: 'smooth'});
        }
        if (rightArrow) {
          rightArrow.onclick = () => container.scrollBy({left: 100, behavior: 'smooth'});
        }
      };
      bindArrows();
      setTimeout(bindArrows, 1000);

      const handleWheel = (event) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
          return;
        }
        event.preventDefault();
        container.scrollBy({ left: event.deltaY, behavior: 'auto' });
        this.updateArrows(container);
      };
      container.addEventListener('wheel', handleWheel, { passive: false });

      let isDragging = false;
      let dragStartX = 0;
      let dragScrollLeft = 0;
      let activePointerId = null;

      const startDrag = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
          return;
        }
        isDragging = false;
        activePointerId = event.pointerId;
        dragStartX = event.clientX;
        dragScrollLeft = container.scrollLeft;
      };

      const moveDrag = (event) => {
        if (activePointerId !== null && event.pointerId !== activePointerId) {
          return;
        }

        const deltaX = event.clientX - dragStartX;

        if (!isDragging) {
          if (activePointerId !== null && Math.abs(deltaX) > 5) {
            isDragging = true;
            container.classList.add('is-dragging');
            try {
              container.setPointerCapture(event.pointerId);
            } catch (e) {}
            dragStartX = event.clientX;
          } else {
            return;
          }
        }

        if (event.cancelable) {
          event.preventDefault();
        }

        const currentDelta = event.clientX - dragStartX;
        const maxScroll = Math.max(
          0,
          container.scrollWidth - container.clientWidth,
        );
        const nextScroll = Math.min(
          Math.max(dragScrollLeft - currentDelta, 0),
          maxScroll,
        );
        container.scrollLeft = nextScroll;
        this.updateArrows(container);
      };

      const endDrag = (event) => {
        if (activePointerId !== null && event.pointerId !== activePointerId) {
          return;
        }
        activePointerId = null;

        if (!isDragging) {
          return;
        }

        isDragging = false;
        container.classList.remove('is-dragging');
        try {
          if (container.hasPointerCapture(event.pointerId)) {
            container.releasePointerCapture(event.pointerId);
          }
        } catch (e) {}
      };

      container.addEventListener('pointerdown', startDrag);
      container.addEventListener('pointermove', moveDrag);
      container.addEventListener('pointerup', endDrag);
      container.addEventListener('pointerleave', endDrag);
      container.addEventListener('pointercancel', endDrag);
      container.addEventListener('scroll', () => this.updateArrows(container));
      window.addEventListener('beforeunload', () => clearInterval(timer), { once: true });
    });
  }

  /**
   * 更新顏色箭頭顯示邏輯：在最左/最右端隱藏對應箭頭。
   * @param {HTMLElement} container
   */
  /**
   * 更新顏色箭頭顯示邏輯：在最左/最右端隱藏對應箭頭。
   * 同時計算右側被切掉的 swatch 數量，顯示在 .swatch-count-badge 上。
   * @param {HTMLElement} container
   */
  updateArrows(container) {
    const swatchInner = container.closest('.swatch-inner');
    const leftArrow = swatchInner
      ? swatchInner.querySelector('.swatch-arrow.left')
      : null;
    const rightArrow = swatchInner
      ? swatchInner.querySelector('.swatch-arrow.right')
      : null;
    const badge = swatchInner ? swatchInner.nextElementSibling : null; // 假設 badge 緊接著 .swatch-scroll-container (或 .swatch-inner, 視結構而定)
    // 根據 liquid 結構: .swatch-scroll-container -> .swatch-count-badge 是 sibling 關係
    // 但上面的 JS 查找是用 container.closest('.swatch-inner').nextElementSibling?
    // 讓我們檢查一下 liquid 結構:
    // <div class='swatch-inner'>
    //    <button left>
    //    <div scroll-container>...</div>
    //    <div badge></div> <-- 插入位置
    //    <button right>
    // </div>
    // 所以 badge 應該是 container.nextElementSibling

    const badgeEl = container.parentElement.querySelector(
      '.swatch-count-badge',
    );

    const scrollLeft = container.scrollLeft;
    const maxScroll = Math.max(
      0,
      container.scrollWidth - container.clientWidth,
    );

    if (leftArrow) {
      leftArrow.style.visibility = scrollLeft <= 2 ? 'hidden' : 'visible';
    }
    if (rightArrow) {
      rightArrow.style.visibility =
        scrollLeft >= maxScroll - 2 ? 'hidden' : 'visible';
    }

    const swatchOuter = container.closest('.swatch-scroll-outer');
    if (swatchOuter) {
      const isOverflowing = maxScroll > 2;
      swatchOuter.classList.toggle('is-overflowing', isOverflowing);
      swatchOuter.classList.toggle('is-at-start', scrollLeft <= 2);
      swatchOuter.classList.toggle('is-at-end', scrollLeft >= maxScroll - 2);
    }

    // 計算右側隱藏的數量
    if (badgeEl) {
      const containerRect = container.getBoundingClientRect();
      const visibleRightEdge = containerRect.right;
      let hiddenCount = 0;

      // 取得所有 swatches (包含 label)
      // 注意構造是 .swatch-scroll-container > .swatches > fieldset > .swatch-input__label (or similar structure in product-variant-options)
      // 或者直接找 .swatches 內的直屬子元素，如果是 div 包裹的話
      // 根據 product-variant-options.liquid:
      // picker_type == 'swatch' -> render 'swatch-input' -> output label.swatch-input__label
      // 讓我們選取所有的 label.swatch-input__label (或者 .product-form__input--swatch 下的 items)
      // container 是 .swatch-scroll-container
      const items = container.querySelectorAll(
        '.swatch-input__label, .product-form__input--swatch label',
      );

      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        // Count any swatch that overflows on the right, including partially clipped items.
        if (itemRect.right > visibleRightEdge + 1) {
          hiddenCount++;
        }
      });

      if (hiddenCount > 0) {
        badgeEl.textContent = `+${hiddenCount}`;
        badgeEl.style.display = '';
      } else {
        badgeEl.style.display = 'none';
      }
    }
  }

  /**
   * 將 swatch 外層寬度限制在卡片內容寬內（扣除左側至容器的距離 *2）。
   * @param {HTMLElement} container
   */
  clampSwatchWidth(container) {
    // Rely on CSS width: 100% to handle this. JS calculation causes instability.
  }

  /**
   * 綁定「標籤捲動列」：寬度限制、箭頭顯示與點擊、滾動時即時更新。
   * 與顏色列一致：每 200ms 檢查一次；1s 後綁定左右鍵；離頁時清理 interval。
   */
  bindTagScrollEvents() {
    document.querySelectorAll('.tag-container').forEach(container => {
      const refresh = () => this.clampTagWidth(container);
      const timer = setInterval(refresh, 200);
      refresh();

      const handleWheel = (event) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
          return;
        }
        event.preventDefault();
        container.scrollBy({ left: event.deltaY, behavior: 'auto' });
      };
      container.addEventListener('wheel', handleWheel, { passive: false });

      let isDragging = false;
      let dragStartX = 0;
      let dragScrollLeft = 0;

      const handlePointerDown = (event) => {
        if (event.pointerType !== 'mouse') {
          return;
        }
        isDragging = true;
        dragStartX = event.clientX;
        dragScrollLeft = container.scrollLeft;
        container.classList.add('is-dragging');
        try {
          container.setPointerCapture(event.pointerId);
        } catch (error) {
          // ignore capture errors
        }
      };

      const handlePointerMove = (event) => {
        if (!isDragging) return;
        event.preventDefault();
        const deltaX = event.clientX - dragStartX;
        container.scrollLeft = dragScrollLeft - deltaX;
      };

      const endDrag = (event) => {
        if (!isDragging) return;
        isDragging = false;
        container.classList.remove('is-dragging');
        try {
          if (typeof container.hasPointerCapture === 'function' && container.hasPointerCapture(event.pointerId)) {
            container.releasePointerCapture(event.pointerId);
          }
        } catch (error) {
          // ignore release errors
        }
      };

      container.addEventListener('pointerdown', handlePointerDown);
      container.addEventListener('pointermove', handlePointerMove);
      container.addEventListener('pointerup', endDrag);
      container.addEventListener('pointerleave', endDrag);
      container.addEventListener('pointercancel', endDrag);

      window.addEventListener('beforeunload', () => clearInterval(timer), { once: true });
    });
  }

  /**
   * 限制標籤容器最大寬度，使其不超出外層。
   * @param {HTMLElement} container
   */
  clampTagWidth(container) {
    const wrap = container.closest('.tag-inner');
    container.style.maxWidth = wrap.clientWidth + 'px';
  }

  /**
   * 將事件來源正規化為實際 input（對 <select> 取 selectedOptions[0]）。
   * @param {Element} target
   * @returns {Element}
   */
  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  /**
   * 取得目前所有選中的 optionValueId 陣列（含 select[option[selected]] 與 fieldset input:checked）。
   * @returns {string[]}
   */
  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({dataset}) => dataset.optionValueId,
    );
  }

  /**
   * 點選 swatch 後，切換「顯示中的名稱」：swatch-selected-name / swatch-color-name 的顯示隱藏。
   */
  bindSwatchNameToggle() {
    const wrappers = this.querySelectorAll('.product-form__input--swatch');
    wrappers.forEach(function (wrapper) {
      wrapper.addEventListener('change', function (e) {
        if (e.target.matches('.swatch-input__input[type=radio]')) {
          // 先全部隱藏
          wrapper.querySelectorAll('.swatch-selected-name').forEach(function (el) {
            el.style.display = 'none';
          });
          wrapper.querySelectorAll('.swatch-color-name').forEach(function (el) {
            el.style.display = 'none';
          });

          // 顯示被選中的名稱（支援兩種外層結構 swatch-outer / swatch-outer2）
          const swatchOuter = e.target.closest('.swatch-outer');
          const swatchOuter2 = e.target.closest('.swatch-outer2');
          if (swatchOuter) {
            const selectedName = swatchOuter.querySelector('.swatch-selected-name');
            if (selectedName) selectedName.style.display = 'block';
          } else if (swatchOuter2) {
            const colorName = swatchOuter2.querySelector('.swatch-color-name');
            if (colorName) colorName.style.display = 'block';
          }
        }
      });
    });
  }

  /**
   * （卡片第一區塊 → 第二區塊）同步 swatch 選擇、更新卡片主圖與連結（?variant=）。
   * 僅整理註解，保留既有選擇策略與 srcset 設定。
   */
  bindSwatchPreviewEvents() {
    const that = this;
    const firstBlock = this.closest('.card-block-first');
    if (!firstBlock) {
      return;
    }
    const selectors = firstBlock.querySelectorAll('input[data-option-value-id]');
    selectors.forEach(select => {
      select.addEventListener('change', (e) => {
        // 在第二區塊找到對應的同 value radio，同步 checked 與觸發變更
        const select2 = that.findAncestorWithClass(e.currentTarget, 'card-block-second')?.querySelector(`.product-form variant-selects input[data-option-value-id="${e.target.dataset.optionValueId}"]`);
        if (select2) {
          that.setRadioChecked(select2);
          that.dispatchChange(select2, {suppressPublish: that.shouldSuppressAutoChange()});
        }

        // 推導出最貼近的變體（先完全匹配，找不到退回比對第 0 層顏色）
        const selectorsChecked = that.querySelectorAll('input[data-option-value-id]:checked');
        const selectedOptions = Array.from(selectorsChecked).map(s => s.value);

        const variants = JSON.parse(that.dataset.variants);
        const foundVariant =
          variants.find(v => selectedOptions.every((opt, i) => v.options[i] === opt))
          || variants.find(v => v.options[0] === selectedOptions[0]);

        // 更新卡片主圖與連結
        if (foundVariant) {
          const img = that.closest('.card--media').querySelector('.card__media img');
          if (img && foundVariant.featured_media) {
            const previewImage = foundVariant.featured_media.preview_image;
            img.src = previewImage.src;
            const imageWidths = [165, 360, 533, 720, 940, 1066];
            let srcset = imageWidths.filter(width => previewImage.width >= width).map(width => `${previewImage.src}&width=${width} ${width}w`).join(', ');
            srcset += `, ${previewImage.src} ${previewImage.width}w`;
            img.srcset = srcset;
          }
          const a = that.closest('.card--media')?.querySelector('.card__media img')?.closest('a');
          if (a) {
            const currentHref = a.getAttribute('href') || that.dataset.productUrl;
            if (currentHref) {
              const nextUrl = new URL(currentHref, window.location.origin);
              nextUrl.searchParams.set('variant', foundVariant.id);
              a.href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
            }
          }
        }
      });
    });
  }

  /**
   * 自下而上尋找包含指定 class 的祖先容器（以「有沒有該 class 的後代」為判定）。
   * @param {Element} startElem
   * @param {string} className
   * @returns {Element|null}
   */
  findAncestorWithClass(startElem, className) {
    let current = startElem;
    while (current) {
      const parent = current.parentElement;
      if (parent && parent.querySelector(`.${className}`)) {
        return parent;
      }
      current = parent;
    }
    return null;
  }

  /**
   * （卡片第二區塊）變體選擇時，立即把正確的 variant id 寫入 ATC input[name="id"]。
   * 僅選擇「可售（available）」且 options 全匹配者。
   */
  switchProductVariant() {
    const secondBlock = this.closest('.card-block-second');
    if (!secondBlock) {
      return;
    }
    const selectors = secondBlock.querySelectorAll('input[data-option-value-id]');
    const that = this;

    selectors.forEach(select => {
      select.addEventListener('change', (e) => {
        const targetRadio = that.querySelectorAll('input[data-option-value-id]:checked');
        that.updateVariantId(targetRadio);
      });
    });
  }

  updateVariantId(targetRadio) {
    const selectorsChecked = targetRadio;
    const variantIdInput = this.closest('.card-block-second')?.querySelector('input[name="id"]');
    console.log( this.closest('.card-block-second'));
    if (!variantIdInput) {
      return;
    }

    const selectedOptions = Array.from(selectorsChecked).map(s => s.value);
    const variants = JSON.parse(this.dataset.variants);
    const foundVariant = variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i]) && v.available,
    );
    if (foundVariant) variantIdInput.value = foundVariant.id;
  }

  /** ---------- 圖片／媒體輔助：開始 ---------- */

  /**
   * 取得主圖 <img>：優先目前可見 slide，其次退回常見 selector。
   * @returns {HTMLImageElement|null}
   */
  getMainImageEl() {
    const visible =
      document.querySelector('.product__media-item.slider__slide[aria-hidden="false"] img') ||
      document.querySelector('.slider__slide[aria-current="true"] img');
    if (visible) return visible;

    return (
      this.closest('.product, product-info, .product-section')?.querySelector(
        '.product__media img.image-magnify-lightbox, .product__media img',
      ) ||
      this.closest('.card--media')?.querySelector('.card__media img')
    );
  }

  /**
   * 等主圖節點可用（在 DOM 且可寫入）才 resolve。
   * @returns {Promise<HTMLImageElement|null>}
   */
  whenMainImageReady() {
    const img = this.getMainImageEl();
    if (img) return Promise.resolve(img);

    return new Promise(resolve => {
      const mo = new MutationObserver(() => {
        const el = this.getMainImageEl();
        if (el) {
          mo.disconnect();
          resolve(el);
        }
      });
      mo.observe(document.documentElement, {childList: true, subtree: true});
      setTimeout(() => {
        mo.disconnect();
        resolve(this.getMainImageEl());
      }, 30000);
    });
  }

  /**
   * 安全切主圖：預載與 decode、避免舊請求覆蓋新選擇、一次到位寫入 src/srcset。
   * @param {string} imageUrl
   * @param {number[]} widths
   */
  async swapMainImageSafe(imageUrl, widths = [246, 493, 600, 823, 1100, 1445, 1646, 1946]) {
    if (!imageUrl) {
      return;
    }

    // 記錄最新目標，防止競態覆寫
    this._latestImageUrl = imageUrl;

    // 預載
    const preload = new Image();
    preload.src = `${imageUrl}?width=${Math.max(...widths)}`;
    try {
      if (preload.decode) await preload.decode();
      else await new Promise((res, rej) => {
        preload.onload = res;
        preload.onerror = rej;
      });
    } catch (_) { /* 忽略，仍嘗試套用 */
    }

    // 等主圖可用
    const img = await this.whenMainImageReady();
    if (!img) {
      return;
    }
    if (this._latestImageUrl !== imageUrl) {
      return;
    }

    // 避免重複寫入
    const srcset = widths.map(w => `${imageUrl}?width=${w} ${w}w`).join(', ');
    if (img.dataset.currentSrc === imageUrl) {
      return;
    }

    img.setAttribute('fetchpriority', 'high');
    img.loading = 'eager';
    img.src = `${imageUrl}?width=${Math.max(...widths)}`;
    img.srcset = srcset;
    img.sizes = img.sizes || '(min-width: 1200px) 605px, (min-width: 990px) 55vw, 100vw';
    img.decoding = 'async';
    img.dataset.currentSrc = imageUrl;
  }

  /**
   * 等待含指定 mediaId 的 slide 出現（支援 data-media-id 尾碼比對）。
   * @param {string|number} mediaId
   * @param {{timeout?: number}} param1
   * @returns {Promise<Element|null>}
   */
  async waitForSlideByMediaId(mediaId, {timeout = 5000} = {}) {
    const t0 = performance.now?.() ?? Date.now();
    return new Promise(resolve => {
      const tryFind = () => {
        const all = Array.from(document.querySelectorAll(
          `.product__media-item[data-media-id$="-${mediaId}"], .product__media-item[data-media-id="${mediaId}"]`,
        ));
        const main = all.find(el => !el.closest('.product-media-modal,[id^="ProductModal"]'));
        const slide = main || all[0];
        if (slide || ((performance.now?.() ?? Date.now()) - t0) > timeout) {
          resolve(slide || null);
        } else {
          requestAnimationFrame(tryFind);
        }
      };
      tryFind();
    });
  }

  /**
   * 以變體切換主圖：優先呼叫 media-gallery.setActiveMedia → 退回點縮圖 → 最後直接換 <img>。
   * @param {object} variant
   * @returns {Promise<boolean>}
   */
  async ensureActiveMediaByVariant(variant) {
    const mediaId = variant?.featured_media?.id;
    const src = variant?.featured_media?.preview_image?.src;
    if (!mediaId && !src) return false;

    const token = Math.random().toString(36).slice(2);
    this._mediaSwitchToken = token;

    const slide = await this.waitForSlideByMediaId(mediaId, {timeout: 5000});
    if (this._mediaSwitchToken !== token) return false;

    const targetId = slide?.dataset?.mediaId || null;
    const gallery = slide?.closest('media-gallery') || document.querySelector('media-gallery');

    if (gallery && typeof gallery.setActiveMedia === 'function' && targetId) {
      try {
        gallery.setActiveMedia(targetId, true);
        return true;
      } catch (_) {
      }
    }

    if (targetId) {
      const thumb = document.querySelector(`[data-thumbnail-id="${targetId}"]`)
        || document.querySelector(`.thumbnail-list__item [data-target="${targetId}"]`);
      if (thumb) {
        thumb.click();
        return true;
      }
    }

    if (src) {
      await this.swapMainImageSafe(src);
      return true;
    }
    return false;
  }

  /**
   * 把網址上的 ?variant= 更新成目前選到的變體（不跳頁）
   * @param {string|number} variantId
   */
  updateVariantUrl(variantId) {
    if (!variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url.toString());
  }

  /**
   * 讀出目前主圖區可見 slide 的 data-media-id（支援 aria-hidden / aria-current 標記）
   * @returns {string|null}
   */
  getActiveSlideMediaId() {
    const visible = document.querySelector('.product__media-item.slider__slide[aria-hidden="false"]')
      || document.querySelector('.slider__slide[aria-current="true"]');
    return visible?.dataset?.mediaId || null;
  }

  /**
   * 持續嘗試直到 active slide 尾碼 == 目標 mediaId 為止（或超時），處理主題初始化反覆覆寫的情況。
   * @param {object} variant
   * @param {{timeout?: number}} param1
   * @returns {Promise<boolean>}
   */
  forceActiveMediaUntilMatch(variant, {timeout = 3000} = {}) {
    const want = String(variant?.featured_media?.id || '');
    if (!want) return Promise.resolve(false);

    const ok = () => {
      const cur = this.getActiveSlideMediaId();
      return !!cur && (cur.endsWith(`-${want}`) || cur === want);
    };

    return new Promise((resolve) => {
      const start = performance.now?.() ?? Date.now();
      const galleryRoot = document.querySelector('media-gallery') || document;
      const apply = () => {
        this.ensureActiveMediaByVariant(variant);
      };

      const mo = new MutationObserver(() => {
        if (ok()) {
          mo.disconnect();
          resolve(true);
        }
      });

      if (ok()) return resolve(true);
      apply();

      mo.observe(galleryRoot, {subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'aria-current']});

      const tick = () => {
        if (ok()) {
          mo.disconnect();
          resolve(true);
          return;
        }
        const now = performance.now?.() ?? Date.now();
        if (now - start > timeout) {
          mo.disconnect();
          resolve(false);
          return;
        }
        apply();
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  /**
   * 取得當前 section 根節點（優先 #shopify-section-${sectionId}）。
   * @returns {Element|Document}
   */
  getSectionRoot() {
    const sid = this.dataset.sectionId;
    return document.querySelector(`#shopify-section-${sid}`)
      || this.closest('section, .product, product-info, .product-section')
      || document;
  }

  /**
   * 將 variantId 立即寫入此區塊內所有 ATC 表單的 input[name="id"]。
   * @param {string|number} variantId
   */
  updateProductFormVariantId(variantId) {
    if (!variantId) return;
    const root = this.getSectionRoot();
    root.querySelectorAll('input[name="id"]').forEach((el) => {
      el.value = variantId;
    });
  }

  /**
   * 鎖／解鎖顏色 swatch：設定 disabled 與 aria-disabled，並且加上/移除淡化樣式。
   * @param {boolean} disabled
   */
  setSwatchesDisabled(disabled) {
    const wrap = this.querySelector('.product-form__input--swatch') || this;
    const radios = this.querySelectorAll('input[type="radio"][data-option-value-id]');
    radios.forEach(r => {
      r.disabled = !!disabled;
      r.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
    if (disabled) wrap.classList.add('swatch-lock');
    else wrap.classList.remove('swatch-lock');
  }

  /**
   * 以安全方式產生指定寬度的圖片 URL（覆蓋既有 width 參數）。
   * @param {string} imageUrl
   * @param {number} width
   * @returns {string}
   */
  buildImageUrl(imageUrl, width) {
    if (!imageUrl) return '';

    const [base, query = ''] = String(imageUrl).split('?');
    const params = new URLSearchParams(query);
    params.set('width', String(width));

    const nextQuery = params.toString();
    return nextQuery ? `${base}?${nextQuery}` : base;
  }

  /**
   * 依指定寬度清單產生 srcset 字串。
   * @param {string} imageUrl
   * @param {number[]} widths
   * @param {boolean} includeOriginal
   * @returns {string}
   */
  buildImageSrcset(imageUrl, widths, includeOriginal = false) {
    const entries = widths.map((width) => `${this.buildImageUrl(imageUrl, width)} ${width}w`);
    if (includeOriginal && widths.length > 0) {
      const maxWidth = widths[widths.length - 1];
      entries.push(`${imageUrl} ${maxWidth}w`);
    }
    return entries.join(', ');
  }

  /**
   * 取得 lifestyle slider item 的模板（同 section 只建立一次）。
   * @param {string} sectionId
   * @returns {HTMLTemplateElement|null}
   */
  getLifestyleSlideTemplate(sectionId) {
    if (!sectionId) return null;

    const sid = String(sectionId);
    if (this._lifestyleSlideTemplate && this._lifestyleSlideTemplateSectionId === sid) {
      return this._lifestyleSlideTemplate;
    }

    const template = document.createElement('template');
    template.innerHTML = `
      <li class="product__media-item grid__item slider__slide">
        <div class="product-media-container media-type-image media-fit-contain global-media-settings gradient constrain-height" style="--ratio: 1.0; --preview-ratio: 1.0;">
          <modal-opener class="product__modal-opener product__modal-opener--image" data-modal="#ProductModal-${sid}">
            <span class="product__media-icon motion-reduce quick-add-hidden product__media-icon--lightbox" aria-hidden="true">
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 19 19">
                  <path fill="currentColor" fill-rule="evenodd" d="M4.667 7.94a.5.5 0 0 1 .499-.501l5.534-.014a.5.5 0 1 1 .002 1l-5.534.014a.5.5 0 0 1-.5-.5" clip-rule="evenodd"/>
                  <path fill="currentColor" fill-rule="evenodd" d="M7.926 4.665a.5.5 0 0 1 .501.498l.014 5.534a.5.5 0 1 1-1 .003l-.014-5.534a.5.5 0 0 1 .499-.501" clip-rule="evenodd"/>
                  <path fill="currentColor" fill-rule="evenodd" d="M12.832 3.03a6.931 6.931 0 1 0-9.802 9.802 6.931 6.931 0 0 0 9.802-9.802M2.323 2.323a7.931 7.931 0 0 1 11.296 11.136l4.628 4.628a.5.5 0 0 1-.707.707l-4.662-4.662A7.932 7.932 0 0 1 2.323 2.323" clip-rule="evenodd"/>
                </svg>
              </span>
            </span>
            <div class="loading__spinner hidden">
              <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/>
              </svg>
            </div>
            <div class="product__media media media--transparent">
              <img width="1946" height="1946" class="image-magnify-lightbox" loading="lazy" alt="" />
            </div>
            <button class="product__media-toggle quick-add-hidden product__media-zoom-lightbox" type="button" aria-haspopup="dialog">
              <span class="visually-hidden">Open image in modal</span>
            </button>
          </modal-opener>
        </div>
      </li>
    `.trim();

    this._lifestyleSlideTemplate = template;
    this._lifestyleSlideTemplateSectionId = sid;
    return template;
  }

  /**
   * 建立桌機 lifestyle 圖片節點。
   * @param {string} imageUrl
   * @returns {HTMLImageElement}
   */
  createLifestyleDesktopImage(imageUrl) {
    const img = document.createElement('img');
    img.className = 'only-desktop';
    img.src = this.buildImageUrl(imageUrl, 600);
    img.srcset = this.buildImageSrcset(imageUrl, [246, 491, 600]);
    img.sizes = '(min-width: 750px) 50vw, 100vw';
    img.alt = '';
    img.loading = 'lazy';
    img.width = 600;
    img.height = 600;
    return img;
  }

  /**
   * Creates a Shopify-hosted lifestyle video player for a product-page placement.
   * @param {Object} lifestyleVideo
   * @param {string} placement
   * @returns {HTMLElement|null}
   */
  createLifestyleVideoPlayer(lifestyleVideo, placement) {
    const sources = Array.isArray(lifestyleVideo?.sources)
      ? lifestyleVideo.sources.filter((source) => source?.src)
      : [];
    if (!sources.length) return null;

    const wrapper = document.createElement('lifestyle-video');
    wrapper.className = `lifestyle-video lifestyle-video--${placement}`;

    const video = document.createElement('video');
    video.className = 'lifestyle-video__media';
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.setAttribute('aria-label', 'Lifestyle video');
    if (lifestyleVideo.preview_image) {
      video.poster = lifestyleVideo.preview_image;
    }

    sources.forEach((videoSource) => {
      const source = document.createElement('source');
      source.src = videoSource.src;
      if (videoSource.type) source.type = videoSource.type;
      video.appendChild(source);
    });

    wrapper.appendChild(video);
    return wrapper;
  }

  createLifestyleDesktopVideo(lifestyleVideo) {
    const player = this.createLifestyleVideoPlayer(lifestyleVideo, 'desktop');
    const mediaId = lifestyleVideo?.id;
    if (!player || mediaId === undefined || mediaId === null) return null;

    const gridItem = document.createElement('div');
    gridItem.className = 'only-desktop lifestyle-video-grid-item';
    gridItem.appendChild(player);
    return gridItem;
  }

  createLifestyleVideoSlideItem(lifestyleVideo, sectionId) {
    const mediaId = lifestyleVideo?.id;
    if (!sectionId || mediaId === undefined || mediaId === null) return null;

    const player = this.createLifestyleVideoPlayer(lifestyleVideo, 'slider');
    if (!player) return null;

    const template = document.createElement('template');
    template.innerHTML = `
      <li class="product__media-item grid__item slider__slide product__media-item--full only-mobile">
        <div class="product-media-container media-type-video media-fit-cover global-media-settings gradient constrain-height">
          <modal-opener class="product__modal-opener product__modal-opener--image" data-modal="#ProductModal-${sectionId}">
            <div class="product__media media media--transparent"></div>
            <button class="product__media-toggle quick-add-hidden product__media-zoom-lightbox lifestyle-video__modal-button" type="button" aria-haspopup="dialog">
              <span class="visually-hidden">Open lifestyle video in modal</span>
            </button>
          </modal-opener>
        </div>
      </li>
    `.trim();

    const li = template.content.firstElementChild;
    if (!li) return null;

    li.id = `Slide-${sectionId}-${mediaId}`;
    li.dataset.mediaId = `${sectionId}-${mediaId}`;

    const ratio = Number(lifestyleVideo.aspect_ratio) || 1;
    const mediaContainer = li.querySelector('.product-media-container');
    mediaContainer?.style.setProperty('--ratio', String(ratio));
    mediaContainer?.style.setProperty('--preview-ratio', String(ratio));
    li.querySelector('.product__media')?.appendChild(player);

    const toggleButton = li.querySelector('.product__media-toggle');
    if (toggleButton) toggleButton.dataset.mediaId = String(mediaId);

    return li;
  }

  createLifestyleModalVideo(lifestyleVideo) {
    const mediaId = lifestyleVideo?.id;
    if (mediaId === undefined || mediaId === null) return null;

    const player = this.createLifestyleVideoPlayer(lifestyleVideo, 'modal');
    if (!player) return null;

    const modalItem = document.createElement('div');
    modalItem.className = 'lifestyle-video-modal global-media-settings global-media-settings--no-shadow';
    modalItem.dataset.mediaId = String(mediaId);
    modalItem.appendChild(player);
    return modalItem;
  }

  /**
   * 建立 slider lifestyle 項目節點。
   * @param {{src?: string, media_id?: string|number}} lifestyleImage
   * @param {string} sectionId
   * @returns {HTMLLIElement|null}
   */
  createLifestyleSlideItem(lifestyleImage, sectionId) {
    const imageUrl = lifestyleImage?.src;
    const mediaId = lifestyleImage?.media_id;
    if (!imageUrl || mediaId === undefined || mediaId === null) return null;

    const template = this.getLifestyleSlideTemplate(sectionId);
    if (!template?.content?.firstElementChild) return null;

    const li = template.content.firstElementChild.cloneNode(true);
    li.id = `Slide-${sectionId}-${mediaId}`;
    li.dataset.mediaId = `${sectionId}-${mediaId}`;

    const img = li.querySelector('img');
    if (img) {
      img.src = this.buildImageUrl(imageUrl, 1946);
      img.srcset = this.buildImageSrcset(imageUrl, [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946]);
      img.sizes =
        '(min-width: 1200px) 605px, (min-width: 990px) calc(55.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw / 1 - 4rem)';
    }

    const toggleBtn = li.querySelector('.product__media-toggle');
    if (toggleBtn) {
      toggleBtn.dataset.mediaId = mediaId;
    }

    return li;
  }

  /**
   * 建立 modal lifestyle 圖片節點。
   * @param {{src?: string, media_id?: string|number}} lifestyleImage
   * @returns {HTMLImageElement}
   */
  createLifestyleModalImage(lifestyleImage) {
    const imageUrl = lifestyleImage?.src;
    const mediaId = lifestyleImage?.media_id;
    const img = document.createElement('img');
    img.className = 'global-media-settings global-media-settings--no-shadow';
    img.src = this.buildImageUrl(imageUrl, 1445);
    img.srcset = this.buildImageSrcset(imageUrl, [550, 1100, 1445, 1680, 2048], true);
    img.sizes = '(min-width: 750px) calc(100vw - 22rem), 1100px';
    img.alt = '';
    img.loading = 'lazy';
    img.width = 1100;
    img.height = 1100;
    if (mediaId !== undefined && mediaId !== null) {
      img.dataset.mediaId = String(mediaId);
    }
    return img;
  }

  /** ---------- 圖片／媒體輔助：結束 ---------- */

  /**
   * 監聽顏色 swatch（radio）變更：
   * - 推導 matchedVariant
   * - 同步 ATC variant id、URL ?variant=
   * - 臨時鎖 swatch，切換主圖（含重試與糾正），最後解鎖
   * - 動態重建 lifestyle 圖片清單（桌機/Modal 版本）
   */
  watchColorSwatchAndUpdateSceneImage() {
    const productInfo = this.closest('product-info');
    if (!productInfo || !productInfo.dataset?.url || !this.dataset?.productUrl) return;
    if (this.dataset.productUrl !== productInfo.dataset.url) return;

    const colorInputs = this.querySelectorAll('input[data-option-value-id][type="radio"]');
    if (!colorInputs.length) return;

    const variants = this.getVariantData();
    if (!variants?.length) return;
    const sectionId = this.dataset.sectionId;

    colorInputs.forEach((input) => {
      input.addEventListener('change', async () => {
        // 本次變更 token：避免舊請求晚到覆蓋新選擇
        const token = Math.random().toString(36).slice(2);
        this._variantChangeToken = token;

        const selectedOptionIds = Array.from(
          this.querySelectorAll('input[data-option-value-id]:checked'),
        ).map((el) => el.value);

        // 優先完全匹配；找不到退回僅比對第 0 階層（顏色）
        const matchedVariant =
          variants.find((variant) => selectedOptionIds.every((opt, index) => variant.options[index] === opt)) ||
          variants.find((variant) => variant.options[0] === selectedOptionIds[0]);

        if (!matchedVariant) {
          return;
        }
        if (this._variantChangeToken !== token) {
          return;
        }

        // ① 同步 ATC 表單
        this.updateProductFormVariantId(matchedVariant.id);
        // ② 同步 URL
        this.updateVariantUrl(matchedVariant.id);
        // ③ 上鎖（4 秒保險自動解）
        try {
          // 若 gallery 尚未就緒或當前不是該變體：嘗試切換／糾正
          if (!(this._galleryReady && this._activeLifestyleVariantId === matchedVariant.id)) {
            await this.ensureActiveMediaByVariant(matchedVariant);
            await this.forceActiveMediaUntilMatch(matchedVariant);
          }
          this._activeLifestyleVariantId = matchedVariant.id;
          this._galleryReady = true;
        } finally {
          // 確保解鎖並清掉保險計時
          // Keep swatches interactive during media swaps.
        }

        // Lifestyle 圖片區塊（桌機獨立區＋Slider＋Modal）
        const lifestyleVideo = matchedVariant.lifestyle_video?.sources?.length
          ? matchedVariant.lifestyle_video
          : null;
        const lifestyleImages = Array.isArray(matchedVariant.lifestyle_images)
          ? matchedVariant.lifestyle_images.slice(0, lifestyleVideo ? 3 : 4)
          : [];

        const customDesignWrapper = productInfo.querySelector('.custom-design-images');
        const sliderWrapper = productInfo.querySelector('#SliderLifestyleImages');
        const productModal = sectionId ? document.getElementById(`ProductModal-${sectionId}`) : null;
        const lifestyleWrapper = productModal?.querySelector('#ProductModalLifestyleImages');

        const desktopFragment = document.createDocumentFragment();
        const sliderFragment = document.createDocumentFragment();
        const modalFragment = document.createDocumentFragment();

        if (lifestyleVideo) {
          const desktopVideo = this.createLifestyleDesktopVideo(lifestyleVideo);
          if (desktopVideo) desktopFragment.appendChild(desktopVideo);

          const sliderVideo = this.createLifestyleVideoSlideItem(lifestyleVideo, sectionId);
          if (sliderVideo) sliderFragment.appendChild(sliderVideo);

          const modalVideo = this.createLifestyleModalVideo(lifestyleVideo);
          if (modalVideo) modalFragment.appendChild(modalVideo);
        }

        lifestyleImages.forEach((lifestyleImage) => {
          const imageUrl = lifestyleImage?.src;
          if (!imageUrl) return;

          if (customDesignWrapper) {
            desktopFragment.appendChild(this.createLifestyleDesktopImage(imageUrl));
          }

          if (sliderWrapper) {
            const slide = this.createLifestyleSlideItem(lifestyleImage, sectionId);
            if (slide) sliderFragment.appendChild(slide);
          }

          if (lifestyleWrapper) {
            modalFragment.appendChild(this.createLifestyleModalImage(lifestyleImage));
          }
        });

        if (customDesignWrapper) customDesignWrapper.replaceChildren(desktopFragment);
        if (sliderWrapper) {
          sliderWrapper.replaceChildren(sliderFragment);

          // 讓 SliderComponent 立即同步最新 lifestyle 圖片。
          const galleryViewer = sectionId ? document.getElementById(`GalleryViewer-${sectionId}`) : null;
          if (galleryViewer && typeof galleryViewer.resetPages === 'function') {
            galleryViewer.resetPages();
          }
        }
        if (lifestyleWrapper) lifestyleWrapper.replaceChildren(modalFragment);
      });
    });
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      {rootMargin: '0px 0px 400px 0px'},
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
    .then((response) => response.text())
    .then((text) => {
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('product-recommendations');

      if (recommendations?.innerHTML.trim().length) {
        this.innerHTML = recommendations.innerHTML;
      }

      if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
        this.remove();
      }

      if (html.querySelector('.grid__item')) {
        this.classList.add('product-recommendations--loaded');
      }
    })
    .catch((e) => {
      console.error(e);
    });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({id, quantity});

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`,
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`,
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`,
    );
  }
}

class ToggleAddOptions extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._isBound) return; // 防止重複綁定
    this._isBound = true;
    this.addEventListener('click', this._toggleHandler);
  }

  _toggleHandler() {
    let stopAt = this.findAncestorWithClass(this, 'card-block-second');
    if (stopAt) {
      stopAt.querySelector('.card-block-second').classList.toggle('is-open');
    }
  };

  findAncestorWithClass(startElem, className) {
    let current = startElem;
    while (current) {
      const parent = current.parentElement;
      if (parent && parent.querySelector(`.${className}`)) {
        return parent;
      }
      current = parent;
    }
    return null;
  }
}

customElements.define('toggle-add-options', ToggleAddOptions);
